import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Pencil } from 'lucide-react'
import {
  apiError,
  isNotFound,
  useAutoAddPreviewQuery,
  useLicensesQuery,
  usePartnerQuery,
  useProvisionLicenseMutation,
  useRegenerateLinkMutation,
  useRenameLicenseMutation,
  useRevokeLicenseMutation,
  useSummaryQuery,
} from '@/store/api'
import type { License, Partner, ProvisionResult } from '@shared/types'
import { PageHeader } from '@/components/layout/AppShell'
import { Badge, StatusBadge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CopyField } from '@/components/ui/copy-field'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  Dialog,
  DialogContent,
} from '@/components/ui/dialog'
import { Input, Label } from '@/components/ui/input'
import { Term } from '@/components/ui/term'
import { Tooltip } from '@/components/ui/tooltip'
import { Select } from '@/components/ui/select'
import { Table, THead, Th, SortableTh, Tr, Td, Pagination, SkeletonRows, type SortDir } from '@/components/ui/data-table'
import { EmptyState, ErrorState } from '@/components/ui/empty-state'
import { NotFound } from '@/pages/NotFound'
import { fmtDate, fmtNum, toDateParam } from '@/lib/format'
import { cn } from '@/lib/utils'

const LABEL_MAX = 120
const PLAN_LABELS = { agency: 'Agency', pro: 'Pro' } as const

/** Why new licences can't be added for this brand right now — mirrors licenceBlocker() server-side. */
function addBlockedReason(p: Partner | undefined): string | null {
  if (!p) return null
  if (p.archivedAt) return 'This brand is archived.'
  if (p.suspension) return 'This brand is suspended — new licences cannot be added.'
  if (!p.offers.agency) return 'The Agency bundle offer is not enabled for this brand.'
  return null
}

function announceAutoAdd(result: ProvisionResult) {
  if (result.autoAdded) {
    toast.info(
      `Licence Entitlements increased from ${fmtNum(result.autoAdded.from)} to ${fmtNum(result.autoAdded.to)}. The extra block is billed pro-rata for this period.`,
    )
  }
}

export function Licences() {
  const { slug = '' } = useParams()
  const { data: summary } = useSummaryQuery(slug)
  const { data: partner } = usePartnerQuery(slug)
  const blocked = addBlockedReason(partner)
  const [status, setStatus] = useState('all')
  const [search, setSearch] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [sort, setSort] = useState('provisionedAt')
  const [dir, setDir] = useState<SortDir>('desc')
  const [page, setPage] = useState(1)
  const [provisionOpen, setProvisionOpen] = useState(false)
  const [linkReveal, setLinkReveal] = useState<License | null>(null)

  const { data, isLoading, isError, error, refetch } = useLicensesQuery({
    slug,
    status: status === 'all' ? undefined : status,
    search: search || undefined,
    from: from || undefined,
    to: to || undefined,
    sort,
    dir,
    page,
  })

  const onSort = (field: string, nextDir: SortDir) => {
    setSort(field)
    setDir(nextDir)
    setPage(1)
  }

  const hasAny = (data?.total ?? 0) > 0 || status !== 'all' || !!search || !!from || !!to

  if (isError && isNotFound(error)) return <NotFound />

  return (
    <>
      <PageHeader
        title="Agency licences"
        description={
          summary ? (
            <>
              {fmtNum(summary.entitlements.inUse)} of {fmtNum(summary.entitlements.held)}{' '}
              <Term id="licence-entitlement">Licence Entitlements</Term> in use: {fmtNum(summary.entitlements.granted)} granted,{' '}
              {fmtNum(summary.entitlements.pendingLive)} pending with a live link
              {summary.entitlements.returning > 0 &&
                `, ${fmtNum(summary.entitlements.returning)} ended this period and returning ${fmtDate(summary.entitlements.lapsesAt)}`}
              .
            </>
          ) : undefined
        }
        actions={<AddLicenceButton blocked={blocked} onClick={() => setProvisionOpen(true)} />}
      />

      {/* Filters */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Select
          ariaLabel="Status filter"
          value={status}
          onValueChange={(v) => {
            setStatus(v)
            setPage(1)
          }}
          options={[
            { value: 'all', label: 'All statuses' },
            { value: 'pending', label: 'Pending' },
            { value: 'active', label: 'Active' },
            { value: 'revoked', label: 'Revoked' },
          ]}
        />
        <Input
          placeholder="Search by label…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(1)
          }}
          className="max-w-xs"
        />
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <span>Granted</span>
          <Input
            type="date"
            aria-label="Granted from (UTC)"
            max={to || toDateParam(new Date())}
            value={from}
            onChange={(e) => {
              setFrom(e.target.value)
              setPage(1)
            }}
            className="w-auto"
          />
          <span>to</span>
          <Input
            type="date"
            aria-label="Granted to (UTC)"
            min={from || undefined}
            value={to}
            onChange={(e) => {
              setTo(e.target.value)
              setPage(1)
            }}
            className="w-auto"
          />
          {(from || to) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFrom('')
                setTo('')
                setPage(1)
              }}
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {isError ? (
        <ErrorState onRetry={refetch} />
      ) : !isLoading && !hasAny ? (
        <EmptyState
          title="No licences yet"
          message="Add your first licence to get an activation link you can send to an agency."
          action={<AddLicenceButton blocked={blocked} onClick={() => setProvisionOpen(true)} />}
        />
      ) : (
        <>
          <Table>
            <THead>
              <SortableTh label="Label" field="label" active={sort === 'label'} dir={dir} onSort={onSort} />
              <Th>Plan</Th>
              <SortableTh label="Status" field="status" active={sort === 'status'} dir={dir} onSort={onSort} />
              <SortableTh label="Granted" field="provisionedAt" active={sort === 'provisionedAt'} dir={dir} onSort={onSort} />
              <SortableTh label="Activated" field="activatedAt" active={sort === 'activatedAt'} dir={dir} onSort={onSort} />
              <Th right>Credits this month</Th>
              <Th right>Actions</Th>
            </THead>
            {isLoading ? (
              <SkeletonRows cols={7} />
            ) : (
              <tbody>
                {data!.rows.length === 0 && (
                  <Tr>
                    <Td colSpan={7} className="text-center text-muted-foreground">
                      No licences match these filters.
                    </Td>
                  </Tr>
                )}
                {data!.rows.map((l) => (
                  <LicenceRow key={l.id} licence={l} slug={slug} />
                ))}
              </tbody>
            )}
          </Table>
          {data && <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPage={setPage} />}
        </>
      )}

      <ProvisionDialog
        slug={slug}
        open={provisionOpen}
        onOpenChange={setProvisionOpen}
        onProvisioned={(r) => {
          announceAutoAdd(r)
          setLinkReveal(r.license)
        }}
      />

      {/* Link reveal pane */}
      <Dialog open={!!linkReveal} onOpenChange={(o) => !o && setLinkReveal(null)}>
        <DialogContent title="Activation link" className="max-w-lg">
          {linkReveal?.activationUrl && <CopyField value={linkReveal.activationUrl} toastMessage="Activation link copied — valid for 14 days." />}
          <p className="mt-3 text-xs text-muted-foreground">
            Single-use · expires {fmtDate(linkReveal?.activationTokenExpiresAt)}. Send it to the agency through your own channels.
          </p>
          <Button variant="secondary" className="mt-4 w-full" onClick={() => setLinkReveal(null)}>
            Done
          </Button>
        </DialogContent>
      </Dialog>
    </>
  )
}

function AddLicenceButton({ blocked, onClick }: { blocked: string | null; onClick: () => void }) {
  if (!blocked) return <Button onClick={onClick}>Add licence</Button>
  return (
    <Tooltip content={blocked}>
      <span tabIndex={0}>
        <Button disabled>Add licence</Button>
      </span>
    </Tooltip>
  )
}

function LicenceRow({ licence, slug }: { licence: License; slug: string }) {
  const [rename, { isLoading: renaming }] = useRenameLicenseMutation()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(licence.label)

  const commit = async () => {
    setEditing(false)
    if (draft.trim() === licence.label) return
    try {
      await rename({ slug, id: licence.id, label: draft.trim() }).unwrap()
      toast.success('Label updated')
    } catch (err) {
      setDraft(licence.label)
      toast.error(apiError(err))
    }
  }

  const returnsLater = licence.entitlementReturnsAt && licence.entitlementReturnsAt > new Date().toISOString()

  return (
    <Tr>
      <Td className="max-w-[22rem]">
        {editing ? (
          <Input
            autoFocus
            maxLength={LABEL_MAX}
            value={draft}
            disabled={renaming}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit()
              if (e.key === 'Escape') {
                setDraft(licence.label)
                setEditing(false)
              }
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            aria-label={`Edit label${licence.label ? ` for ${licence.label}` : ''}`}
            className="group inline-flex max-w-full items-center gap-1.5 text-left"
          >
            <span className={cn('truncate', licence.label ? 'font-medium' : 'text-muted-foreground')}>
              {licence.label || 'Add label'}
            </span>
            <Pencil className="size-3.5 shrink-0 text-muted-foreground opacity-40 group-hover:opacity-100" />
          </button>
        )}
      </Td>
      <Td>{PLAN_LABELS[licence.plan]}</Td>
      <Td>
        <span className="inline-flex flex-wrap items-center gap-1.5">
          <StatusBadge status={licence.status} />
          {licence.activationExpired && <Badge variant="expired">Link expired</Badge>}
        </span>
        {licence.status === 'revoked' && returnsLater && (
          <div className="mt-1 text-xs text-muted-foreground">Entitlement returns {fmtDate(licence.entitlementReturnsAt)}</div>
        )}
      </Td>
      <Td>{fmtDate(licence.provisionedAt)}</Td>
      <Td className={licence.activatedAt ? '' : 'text-muted-foreground'}>{fmtDate(licence.activatedAt)}</Td>
      <Td right>
        {licence.credits ? (
          <Tooltip
            content={`${fmtNum(licence.credits.allocated)} allocated this month + ${fmtNum(licence.credits.carriedOver)} carried over from last month`}
          >
            <span className="whitespace-nowrap">
              {fmtNum(licence.credits.used)} / {fmtNum(licence.credits.allocated + licence.credits.carriedOver)}
              {licence.credits.carriedOver > 0 && (
                <span className="block text-xs text-muted-foreground">incl. {fmtNum(licence.credits.carriedOver)} carried over</span>
              )}
            </span>
          </Tooltip>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </Td>
      <Td right>
        <div className="flex justify-end gap-2">
          {licence.status === 'pending' && !licence.activationExpired && licence.activationUrl && (
            <Button
              variant="secondary"
              size="sm"
              onClick={async () => {
                await navigator.clipboard.writeText(licence.activationUrl!)
                toast.success('Activation link copied — valid for 14 days.')
              }}
            >
              Copy link
            </Button>
          )}
          {licence.status === 'pending' && licence.activationExpired && <RegenerateButton slug={slug} licence={licence} />}
          {licence.status !== 'revoked' && <RevokeButton slug={slug} licence={licence} />}
        </div>
      </Td>
    </Tr>
  )
}

/**
 * An expired link holds no Entitlement, so a fresh one may push the brand over what it
 * holds. When it would, confirm first and say what it costs.
 */
function RegenerateButton({ slug, licence }: { slug: string; licence: License }) {
  const [regenerate, { isLoading }] = useRegenerateLinkMutation()
  const { data: preview } = useAutoAddPreviewQuery(slug)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const run = async () => {
    try {
      const result = await regenerate({ slug, id: licence.id }).unwrap()
      setConfirmOpen(false)
      announceAutoAdd(result)
      if (result.license.activationUrl) {
        await navigator.clipboard.writeText(result.license.activationUrl)
        toast.success('New link generated and copied — valid for 14 days.')
      }
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  return (
    <>
      <Button variant="secondary" size="sm" loading={isLoading} onClick={() => (preview?.autoAdd ? setConfirmOpen(true) : run())}>
        Regenerate link
      </Button>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent title="This will add Licence Entitlements">
          <AlertDialogDescription>
            {preview?.autoAdd && <AutoAddExplainer from={preview.autoAdd.from} to={preview.autoAdd.to} />}
          </AlertDialogDescription>
          <div className="mt-5 flex justify-end gap-2">
            <AlertDialogCancel asChild>
              <Button variant="secondary">Cancel</Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                loading={isLoading}
                onClick={(e) => {
                  e.preventDefault()
                  void run()
                }}
              >
                Regenerate link
              </Button>
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function AutoAddExplainer({ from, to }: { from: number; to: number }) {
  return (
    <p>
      All {fmtNum(from)} <Term id="licence-entitlement">Licence Entitlements</Term> are in use. Continuing raises them to{' '}
      <strong>{fmtNum(to)}</strong>. The extra {fmtNum(to - from)} are billed pro-rata for the rest of this Billing Period and in
      full from next period. Nothing is blocked — Atarim will confirm the new volume with you.
    </p>
  )
}

/** Canonical destructive confirm — plain-language consequences (design.md §5.6). */
function RevokeButton({ slug, licence }: { slug: string; licence: License }) {
  const [open, setOpen] = useState(false)
  const [revoke, { isLoading }] = useRevokeLicenseMutation()
  const pending = licence.status === 'pending'
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <Button variant="ghost" size="sm" className="text-ata-red hover:bg-ata-red/10" onClick={() => setOpen(true)}>
        {pending ? 'Cancel' : 'Revoke'}
      </Button>
      <AlertDialogContent title={pending ? 'Cancel this licence?' : 'Revoke this licence?'}>
        <AlertDialogDescription>
          {pending ? (
            <p>
              The activation link stops working <strong>immediately</strong> and can't be used to create an account. If the link was
              still live, its Licence Entitlement returns to your pool from the start of next Billing Period.
            </p>
          ) : (
            <p>
              The agency's Atarim workspace loses its plan and monthly credits <strong>immediately</strong>. Their data is kept and their
              account drops to the free plan. The Licence Entitlement returns to your pool from the start of next Billing Period. This
              cannot be undone from this dashboard.
            </p>
          )}
        </AlertDialogDescription>
        <div className="mt-5 flex justify-end gap-2">
          <AlertDialogCancel asChild>
            <Button variant="secondary">Keep licence</Button>
          </AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button
              variant="destructive"
              loading={isLoading}
              onClick={async (e) => {
                e.preventDefault()
                try {
                  await revoke({ slug, id: licence.id }).unwrap()
                  toast.success(pending ? 'Licence cancelled' : 'Licence revoked')
                  setOpen(false)
                } catch (err) {
                  toast.error(apiError(err))
                }
              }}
            >
              {pending ? 'Cancel licence' : 'Revoke licence'}
            </Button>
          </AlertDialogAction>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function ProvisionDialog({
  slug,
  open,
  onOpenChange,
  onProvisioned,
}: {
  slug: string
  open: boolean
  onOpenChange: (o: boolean) => void
  onProvisioned: (r: ProvisionResult) => void
}) {
  const [label, setLabel] = useState('')
  const [provision, { isLoading }] = useProvisionLicenseMutation()
  const { data: preview } = useAutoAddPreviewQuery(slug, { skip: !open })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Add licence" description="Creates a pending licence with a single-use activation link (14-day expiry).">
        <form
          onSubmit={async (e) => {
            e.preventDefault()
            try {
              const result = await provision({ slug, label }).unwrap()
              setLabel('')
              onOpenChange(false)
              onProvisioned(result)
            } catch (err) {
              toast.error(apiError(err))
            }
          }}
          className="space-y-4"
        >
          <div>
            <Label htmlFor="lic-label">Label (optional)</Label>
            <Input
              id="lic-label"
              placeholder="e.g. agency name"
              maxLength={LABEL_MAX}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
          {preview?.autoAdd && (
            <div className="rounded-lg border border-ata-orange/40 bg-ata-orange/10 px-3 py-2 text-sm">
              <AutoAddExplainer from={preview.autoAdd.from} to={preview.autoAdd.to} />
            </div>
          )}
          <Button type="submit" className="w-full" loading={isLoading}>
            {preview?.autoAdd ? `Add licence and raise to ${fmtNum(preview.autoAdd.to)}` : 'Add licence'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
